import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// 註冊 Chart.js 組件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const TechnicalChart = ({ symbol, priceData, width = 300, height = 200 }) => {
  if (!priceData || priceData.length === 0) {
    return (
      <div style={{ 
        width, 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        border: '1px solid #e8e8e8',
        borderRadius: '4px',
        backgroundColor: '#fafafa'
      }}>
        <span style={{ color: '#999', fontSize: '12px' }}>無數據</span>
      </div>
    );
  }

  // 反轉數據順序，讓最新的數據在右邊
  const reversedData = [...priceData].reverse();
  
  // 只顯示最近200個交易日，但使用全部數據計算MA100
  const displayData = reversedData.slice(-200);
  
  // 計算MA100移動平均線（使用全部數據確保計算準確）
  const calculateMA = (data, period) => {
    const ma = [];
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        ma.push(null);
      } else {
        const sum = data.slice(i - period + 1, i + 1).reduce((acc, item) => acc + parseFloat(item.price), 0);
        ma.push(sum / period);
      }
    }
    return ma;
  };
  
  const ma100 = calculateMA(reversedData, 100);
  
  // 只取最近200個交易日的MA100數據
  const displayMA100 = ma100.slice(-200);
  
  // 準備圖表數據
  const chartData = {
    labels: displayData.map(item => {
      const date = new Date(item.date);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    }),
    datasets: [
      {
        label: '收盤價',
        data: displayData.map(item => parseFloat(item.price)),
        borderColor: '#57A300',
        backgroundColor: 'rgba(87, 163, 0, 0.1)',
        borderWidth: 1.5,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 3,
        pointHoverBackgroundColor: '#57A300',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        yAxisID: 'y',
      },
      {
        label: 'MA100',
        data: displayMA100,
        borderColor: '#ff6b35',
        backgroundColor: 'rgba(255, 107, 53, 0.1)',
        borderWidth: 1.5,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 3,
        pointHoverBackgroundColor: '#ff6b35',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        yAxisID: 'y',
      }
    ]
  };

  // 圖表選項
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#57A300',
        borderWidth: 1,
        cornerRadius: 4,
        displayColors: true,
        callbacks: {
          title: function(context) {
            const dataIndex = context[0].dataIndex;
            const originalDate = displayData[dataIndex].date;
            const date = new Date(originalDate);
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          },
          label: function(context) {
            if (context.dataset.label === '收盤價') {
              return `價格: $${parseFloat(context.parsed.y).toFixed(2)}`;
            } else if (context.dataset.label === 'MA100') {
              return `MA100: $${parseFloat(context.parsed.y).toFixed(2)}`;
            }
            return context.dataset.label + ': $' + parseFloat(context.parsed.y).toFixed(2);
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 6,
          maxRotation: 0,
          color: '#666',
          font: {
            size: 10
          }
        },
        border: {
          display: false
        }
      },
      y: {
        display: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          maxTicksLimit: 4,
          color: '#666',
          font: {
            size: 10
          },
          callback: function(value) {
            return '$' + value.toFixed(0);
          }
        },
        border: {
          display: false
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    elements: {
      point: {
        radius: 0
      }
    }
  };

  return (
    <div style={{ 
      width, 
      height, 
      border: '1px solid #e8e8e8', 
      borderRadius: '4px',
      padding: '8px',
      backgroundColor: '#fff'
    }}>
      <div style={{ 
        fontSize: '12px', 
        fontWeight: 'bold', 
        color: '#333', 
        marginBottom: '4px',
        textAlign: 'center'
      }}>
        {symbol}
      </div>
      <div style={{ width: '100%', height: 'calc(100% - 20px)' }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default TechnicalChart; 